import { ListingCategory, ListingFieldConfigKey } from './enums';

const CATEGORY_SIZE_FIELD_KEY = {
  [ListingCategory.DANCEWEAR]: ListingFieldConfigKey.DANCEWEAR_SIZE,
  [ListingCategory.COSTUMES]: ListingFieldConfigKey.COSTUMES_SIZE,
  [ListingCategory.SHOES]: ListingFieldConfigKey.SHOES_SIZE,
};

const SIZE_PUBLIC_DATA_CANDIDATES = [
  'sizeOption',
  'size',
  'sizeLabel',
  'dancewear_sizeOption',
  'dancewear_size',
  'costume_sizeOption',
  'costume_size',
  'shoe_sizeOption',
  'shoe_size',
];

const toTrimmedString = value => {
  if (value == null) {
    return null;
  }

  const asString = `${value}`.trim();
  return asString.length > 0 ? asString : null;
};

export const humanizeOptionValue = value => {
  const normalized = toTrimmedString(value);
  if (!normalized) {
    return null;
  }

  return normalized
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

export const normalizeSizeValue = value => {
  if (Array.isArray(value)) {
    return normalizeSizeValue(value[0]);
  }

  if (value && typeof value === 'object') {
    return normalizeSizeValue(
      value?.values ??
        value?.option ??
        value?.value ??
        value?.key ??
        value?.id ??
        value?.label ??
        value?.name
    );
  }

  return toTrimmedString(value);
};

export const getOptionValue = option =>
  normalizeSizeValue(
    option?.values ?? option?.option ?? option?.value ?? option?.key ?? option?.id ?? null
  );

export const getOptionLabel = option => {
  const label = toTrimmedString(option?.label ?? option?.name);
  if (label) {
    return label;
  }

  const value = getOptionValue(option);
  return humanizeOptionValue(value);
};

const findEnumOptionByValue = (sizeValue, enumOptions = []) => {
  const normalizedSizeValue = normalizeSizeValue(sizeValue);
  if (!normalizedSizeValue || !Array.isArray(enumOptions)) {
    return null;
  }

  return enumOptions.find(option => getOptionValue(option) === normalizedSizeValue) || null;
};

const ensureSizeOptionShape = option => {
  const optionValue = getOptionValue(option);
  if (!optionValue) {
    return null;
  }

  const optionLabel = getOptionLabel(option) || humanizeOptionValue(optionValue);
  return {
    values: optionValue,
    label: optionLabel || optionValue,
  };
};

export const toSizeOption = (sizeInput, enumOptions = []) => {
  const optionFromInput = ensureSizeOptionShape(sizeInput);
  if (optionFromInput) {
    return optionFromInput;
  }

  const normalizedSizeValue = normalizeSizeValue(sizeInput);
  if (!normalizedSizeValue) {
    return null;
  }

  const enumMatch = findEnumOptionByValue(normalizedSizeValue, enumOptions);
  if (enumMatch) {
    return ensureSizeOptionShape(enumMatch);
  }

  return {
    values: normalizedSizeValue,
    label: humanizeOptionValue(normalizedSizeValue) || normalizedSizeValue,
  };
};

export const convertSizesToValidArray = input => {
  if (Array.isArray(input)) {
    return input;
  }

  if (input && typeof input === 'object') {
    return Object.keys(input)
      .map(key => input[key])
      .filter(Boolean);
  }

  return [];
};

const toNonNegativeIntegerOrNull = value => {
  if (value === '' || value == null) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const normalizeVariantId = value => {
  if (!value) {
    return null;
  }

  return typeof value === 'string' ? value : value?.uuid || value?.id?.uuid || null;
};

export const normalizeSizeVariantsForForm = (rawSizes, sizeEnumOptions = []) => {
  const sizeVariants = convertSizesToValidArray(rawSizes);

  return sizeVariants
    .map(variant => {
      const normalizedSizeValue = normalizeSizeValue(
        variant?.size ??
          variant?.sizeOption ??
          variant?.value ??
          variant?.sizeLabel ??
          variant?.selectedSize
      );
      const normalizedSizeOption = toSizeOption(normalizedSizeValue, sizeEnumOptions);
      const stock = toNonNegativeIntegerOrNull(variant?.stock);
      const sku = toTrimmedString(variant?.sku);
      const id = normalizeVariantId(variant?.id);
      const imageIds = Array.isArray(variant?.imageIds) ? variant.imageIds.filter(Boolean) : [];

      return {
        ...(id ? { id } : {}),
        size: normalizedSizeOption?.values || '',
        ...(stock != null ? { stock } : {}),
        ...(sku ? { sku } : {}),
        ...(imageIds.length ? { imageIds } : {}),
      };
    })
    .filter(variant => {
      const hasStock = typeof variant?.stock === 'number';
      return !!(variant?.id || variant?.size || variant?.sku || hasStock);
    });
};

export const normalizeSizeVariantsForPublicData = (rawSizes, sizeEnumOptions = []) => {
  const formVariants = normalizeSizeVariantsForForm(rawSizes, sizeEnumOptions);

  return formVariants
    .map(variant => {
      const sizeOption = toSizeOption(variant?.size, sizeEnumOptions);
      if (!sizeOption) {
        return null;
      }

      const stock = toNonNegativeIntegerOrNull(variant?.stock);
      const sku = toTrimmedString(variant?.sku);
      const imageIds = Array.isArray(variant?.imageIds) ? variant.imageIds.filter(Boolean) : [];

      return {
        ...(variant?.id ? { id: variant.id } : {}),
        size: sizeOption,
        sizeOption,
        ...(stock != null ? { stock } : {}),
        ...(sku ? { sku } : {}),
        ...(imageIds.length ? { imageIds } : {}),
      };
    })
    .filter(Boolean);
};

const dedupeSizeOptions = options => {
  const seenValues = new Set();
  return options.filter(option => {
    const optionValue = normalizeSizeValue(option?.values);
    if (!optionValue || seenValues.has(optionValue)) {
      return false;
    }

    seenValues.add(optionValue);
    return true;
  });
};

export const getPrimarySizeOption = publicData => {
  for (const key of SIZE_PUBLIC_DATA_CANDIDATES) {
    const option = toSizeOption(publicData?.[key]);
    if (option) {
      return option;
    }
  }

  return null;
};

export const getSizeVariantOptions = publicData => {
  const variantSizeOptions = normalizeSizeVariantsForPublicData(publicData?.sizes || []).map(
    variant => variant?.sizeOption
  );
  return dedupeSizeOptions(variantSizeOptions.map(option => toSizeOption(option)).filter(Boolean));
};

export const getListingSizeChoices = publicData => {
  const sizeVariants = getSizeVariantOptions(publicData);
  if (sizeVariants.length > 0) {
    return sizeVariants;
  }

  const primarySize = getPrimarySizeOption(publicData);
  return primarySize ? [primarySize] : [];
};

const getVariantStockBySizeValue = publicData => {
  const variants = normalizeSizeVariantsForPublicData(publicData?.sizes || []);
  return variants.reduce((acc, variant) => {
    const sizeValue = normalizeSizeValue(variant?.sizeOption?.values || variant?.size?.values);
    const stock = toNonNegativeIntegerOrNull(variant?.stock);
    if (!sizeValue || stock == null) {
      return acc;
    }

    const existingStock = acc[sizeValue];
    acc[sizeValue] = Number.isFinite(existingStock) ? Math.max(existingStock, stock) : stock;
    return acc;
  }, {});
};

export const getFirstAvailableSizeOption = publicData => {
  const sizeChoices = getListingSizeChoices(publicData);
  if (!sizeChoices.length) {
    return null;
  }

  const stockBySizeValue = getVariantStockBySizeValue(publicData || {});
  const hasStockData = Object.keys(stockBySizeValue).length > 0;
  if (!hasStockData) {
    return sizeChoices[0];
  }

  const firstInStockOption = sizeChoices.find(option => {
    const optionValue = normalizeSizeValue(option?.values);
    const stock = optionValue ? stockBySizeValue[optionValue] : null;
    return Number.isFinite(stock) && stock > 0;
  });

  return firstInStockOption || sizeChoices[0];
};

export const resolveSelectedSizeOption = (publicData, selectedSize) => {
  const sizeChoices = getListingSizeChoices(publicData);
  if (!sizeChoices.length) {
    return null;
  }

  const normalizedSelectedSize = normalizeSizeValue(selectedSize);
  if (!normalizedSelectedSize) {
    return sizeChoices[0];
  }

  return (
    sizeChoices.find(option => normalizeSizeValue(option?.values) === normalizedSelectedSize) ||
    sizeChoices.find(option => normalizeSizeValue(option?.label) === normalizedSelectedSize) ||
    sizeChoices[0]
  );
};

export const getListingSizeDisplay = publicData => {
  const sizeVariants = getSizeVariantOptions(publicData);
  if (sizeVariants.length > 1) {
    return { isMultiple: true, label: null };
  }

  if (sizeVariants.length === 1) {
    return { isMultiple: false, label: sizeVariants[0]?.label || null };
  }

  const primarySize = getPrimarySizeOption(publicData);
  return { isMultiple: false, label: primarySize?.label || null };
};

export const isSizeVariationEnabled = value => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['yes', 'true', '1'].includes(normalized);
  }

  return false;
};

const getCategorySizeFieldConfigKey = category =>
  CATEGORY_SIZE_FIELD_KEY[category] || ListingFieldConfigKey.SIZE;

export const getSizeEnumOptionsForCategory = ({
  category,
  dancewearCategoryConfig = [],
  costumesCategoryConfig = [],
  shoesCategoryConfig = [],
}) => {
  const categoryConfigs =
    category === ListingCategory.DANCEWEAR
      ? dancewearCategoryConfig
      : category === ListingCategory.COSTUMES
      ? costumesCategoryConfig
      : category === ListingCategory.SHOES
      ? shoesCategoryConfig
      : [];

  const sizeFieldConfigKey = getCategorySizeFieldConfigKey(category);
  const configMatchedByCategory = categoryConfigs.find(config => config?.key === sizeFieldConfigKey);

  if (Array.isArray(configMatchedByCategory?.enumOptions)) {
    return configMatchedByCategory.enumOptions;
  }

  const genericConfig = categoryConfigs.find(config => config?.key === ListingFieldConfigKey.SIZE);
  return Array.isArray(genericConfig?.enumOptions) ? genericConfig.enumOptions : [];
};

export const getSizeEnumOptionsForCategoryFromConfig = ({ category, config }) => {
  const listingFields = Array.isArray(config?.listing?.listingFields) ? config.listing.listingFields : [];
  const categorySizeFieldKey = getCategorySizeFieldConfigKey(category);
  const listingFieldsByCategory = listingFields.filter(field =>
    Array.isArray(field?.categoryConfig?.categoryIds)
      ? field.categoryConfig.categoryIds.includes(category)
      : false
  );
  const fieldCandidates = listingFieldsByCategory.length > 0 ? listingFieldsByCategory : listingFields;

  const sizeFieldConfig =
    fieldCandidates.find(field => field?.key === categorySizeFieldKey) ||
    fieldCandidates.find(field => field?.key === ListingFieldConfigKey.SIZE) ||
    null;

  return Array.isArray(sizeFieldConfig?.enumOptions) ? sizeFieldConfig.enumOptions : [];
};
